import { ChangeEvent } from 'react'

import { Box, FormField, InputSearch } from '@domain-group/suit-theme'
import { matchFilterString } from '@domain-group/javascript-utils'
import { useDebounce, useSetState } from '@domain-group/react-hooks'

import ScreenContent from '@modals/NewOrderModal/components/ScreenContent'
import ScreenHeader from '@modals/NewOrderModal/components/ScreenHeader'

import ServiceIcon from '@components/Icons/ServiceIcon'

import useSelector from '@hooks/useSelector'

import type { UseNewOrderPayload } from '@modals/NewOrderModal'

import type { OrderBundleModel } from '@models/types'
import type { BuildBundleOrderPayload } from '../../functions/buildBundleOrder'

import OrderBundle from './components/OrderBundle'
import type { SetState } from './components/OrderBundle'

type BuildAndCreateOrderParams = ReturnType<UseNewOrderPayload['callbacks']['buildBundleOrder']> & {
  callbacks: {
    createCampaignQuoteItems: UseNewOrderPayload['callbacks']['createCampaignQuoteItems'],
    createCampaignQuoteTemplateItems: UseNewOrderPayload['callbacks']['createCampaignQuoteTemplateItems'],
    createDefaultQuoteItems: UseNewOrderPayload['callbacks']['createDefaultQuoteItems'],
    createOrders: UseNewOrderPayload['callbacks']['createOrders'],
  },
  itemType: 'quoteItem' | 'quoteTemplateItem',
  payload: BuildBundleOrderPayload['payload'],
}

const buildAndCreateOrder = (params: BuildAndCreateOrderParams) => {
  const {
    callbacks: {
      createCampaignQuoteItems,
      createCampaignQuoteTemplateItems,
      createDefaultQuoteItems,
      createOrders,
    },
    itemType,
    payload,
  } = params

  if (itemType === 'quoteItem'){
    // @ts-expect-error TS2345
    return createCampaignQuoteItems('bundle', payload)
  }

  if (itemType === 'quoteTemplateItem'){
    // @ts-expect-error TS2345
    return createCampaignQuoteTemplateItems('bundle', payload)
  }

  if (itemType === 'defaultQuoteItem'){
    // @ts-expect-error TS2345
    return createDefaultQuoteItems(payload)
  }

  return createOrders(payload)
}

const defaultState = {
  filterString: '',
}

interface OrderBundleFlowProps {
  callbacks: {
    addItem: (payload: object) => void,
    closeModal: VoidFunction,
  },
  newOrderPayload: UseNewOrderPayload,
}

const OrderBundleFlow = (props: OrderBundleFlowProps) => {
  const {
    callbacks: {
      addItem,
      closeModal,
    },
    newOrderPayload: {
      callbacks: {
        buildBundleOrder,
        createCampaignQuoteItems,
        createCampaignQuoteTemplateItems,
        createDefaultQuoteItems,
        createOrders,
        resetState,
      },
      filteredAgencyOrderBundles,
    },
  } = props

  const [state, setState] = useSetState(defaultState)
  const { filterString } = state

  const orderBundles = useSelector(reduxState => reduxState.entities.orderBundles)

  const debouncedString = useDebounce(filterString, 50)

  const filteredOrderBundlesByString = filteredAgencyOrderBundles
    .reduce((filteredOrderBundles: OrderBundleModel[], agencyOrderBundle) => {
      const orderBundle = orderBundles[agencyOrderBundle.order_bundle_id] || {}
      const { title } = orderBundle

      if (matchFilterString(title, debouncedString)) filteredOrderBundles.push(orderBundle)

      return filteredOrderBundles
    }, [])

  return (
    <>
      <ScreenHeader
        callbacks={{
          goBack: () => resetState(),
        }}
        imageComponent={(
          <Box
            alignItems="center"
            borderRadius={5}
            border="1px solid"
            borderColor="lineColor"
            height={50}
            justifyContent="center"
            overflow="hidden"
            width={84}
          >
            <ServiceIcon
              fillColor="#8C9FBF"
              height={38}
              serviceKey="bundles"
              width={38}
            />
          </Box>
          )}
      >
        <ScreenHeader.Title>
          Bundles
        </ScreenHeader.Title>

        <ScreenHeader.Subtitle>
          from All Providers
        </ScreenHeader.Subtitle>
      </ScreenHeader>

      <ScreenContent hasFooter={false}>
        <>
          <FormField label="Filter Bundles">
            {/*
             // @ts-expect-error TS2741 */}
            <InputSearch
              onChange={(e: ChangeEvent<HTMLInputElement>) => setState({ filterString: e.target.value })}
              value={filterString}
            />
          </FormField>

          {filteredOrderBundlesByString.map(orderBundle => (
            <OrderBundle
              callbacks={{
                selectOrderBundle: (setButtonState: SetState) => {
                  const addItemPayload = {
                    callbacks: {
                      action: (
                        actionParams: ReturnType<UseNewOrderPayload['callbacks']['buildBundleOrder']>,
                      ) => (
                        // @ts-expect-error TS2345
                        buildAndCreateOrder({
                          ...actionParams,
                          callbacks: {
                            createCampaignQuoteItems,
                            createCampaignQuoteTemplateItems,
                            createDefaultQuoteItems,
                            createOrders,
                          },
                        })
                      ),
                      afterAction: () => {
                        setButtonState({ loading: false })
                        return closeModal()
                      },
                      afterError: () => setButtonState({ loading: false }),
                      buildActionParams: () => {
                        const buildProviderPackageOrderPayload = buildBundleOrder({
                          orderBundleId: orderBundle.id,
                        })

                        return [buildProviderPackageOrderPayload]
                      },
                    },
                    toastText: 'Item Created',
                  }

                  addItem(addItemPayload)
                },
              }}
              key={orderBundle.id}
              orderBundle={orderBundle}
            />
          ))}
        </>
      </ScreenContent>
    </>
  )
}

export default OrderBundleFlow
